import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody } from '../../../common/models/common';
import { RootState } from '../../app.model';
import { booleanLookup } from '../../../common/constants/common';
import { IConfigSoftwareProductState } from './configSoftwareProduct.model';
import {
  deleteConfigSoftwareProduct,
  getConfigSoftwareProductById,
  saveConfigSoftwareProduct,
  searchConfigSoftwareProduct,
} from './configSoftwareProduct.action';
import { IConfigSoftwareProduct } from '../../../services/master/configSoftwareProduct/configSoftwareProduct.model';

export const initialState: IConfigSoftwareProductState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const configSoftwareProductSlice = createSlice({
  name: 'configSoftwareProduct',
  initialState,
  reducers: {
    clearConfigSoftwareProduct: () => {
      return initialState;
    },
    clearConfigSoftwareProductMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearConfigSoftwareProductGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchConfigSoftwareProduct.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchConfigSoftwareProduct.fulfilled.type]: (state, action: PayloadAction<any>) => {
      const { records, total_count, table_name, column_selection } = action.payload;
      state.search.data = records;
      state.search.count = total_count;
      state.search.lookups = { booleanLookup };
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = table_name;
      if (column_selection) {
        state.tableColumnSelection.id = column_selection.id;
        const tableSelectionObj = JSON.parse(column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = table_name;
    },
    [searchConfigSoftwareProduct.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getConfigSoftwareProductById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getConfigSoftwareProductById.fulfilled.type]: (
      state,
      action: PayloadAction<IConfigSoftwareProduct>
    ) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getConfigSoftwareProductById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveConfigSoftwareProduct.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveConfigSoftwareProduct.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveConfigSoftwareProduct.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteConfigSoftwareProduct.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteConfigSoftwareProduct.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteConfigSoftwareProduct.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const configSoftwareProductSelector = (state: RootState) => state.configSoftwareProduct;

// Actions
export const {
  clearConfigSoftwareProduct,
  clearConfigSoftwareProductMessages,
  clearConfigSoftwareProductGetById,
  setTableColumnSelection,
} = configSoftwareProductSlice.actions;

// The reducer
export default configSoftwareProductSlice.reducer;
